import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/v2/lib/rbac";
import { GlassCard, Chip, Skeleton, Modal, Table, PageHead } from "@/v2/admin/ui";
import { Coins, Plus, Minus, ShieldAlert } from "lucide-react";

const TYPE_AR = { checkin: "حضور يومي", challenge: "إكمال تحدٍ", manual: "تعديل يدوي" };

function AdjustModal({ onClose }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ user_id: "", points: 10, reason: "", sign: 1 });
  const [saving, setSaving] = useState(false);
  const { data } = useQuery({ queryKey: ["adm-users-all"], queryFn: async () => (await api.get("/admin/users?limit=500&role=employee")).data });

  const save = async () => {
    if (!f.user_id) return toast.error("اختر الموظف");
    if (!f.reason.trim()) return toast.error("سبب التعديل إلزامي ويُسجَّل في سجل الإدارة");
    setSaving(true);
    try {
      await api.post("/admin/points/adjust", {
        user_id: f.user_id, points: Number(f.points) * f.sign, reason: f.reason.trim(),
      });
      toast.success("تم تعديل النقاط وتسجيل العملية");
      qc.invalidateQueries({ queryKey: ["adm-points"] });
      qc.invalidateQueries({ queryKey: ["adm-users"] });
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر التعديل"); } finally { setSaving(false); }
  };

  return (
    <Modal title="تعديل نقاط موظف" onClose={onClose} testId="points-modal">
      <div className="space-y-3">
        <div>
          <label className="v2-label">الموظف</label>
          <select value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} data-testid="points-user" className="v2-input">
            <option value="">اختر الموظف...</option>
            {(data?.rows || []).map((u) => <option key={u.id} value={u.id}>{`${u.name} — ${u.department || ""} (${u.points} نقطة)`}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setF({ ...f, sign: 1 })} data-testid="points-add" className={`flex-1 h-11 rounded-full text-sm font-bold ${f.sign === 1 ? "bg-[#91cab8] text-[#25343f]" : "bg-white/5 border border-white/10 text-[#bcc8cf]"}`}><Plus size={14} className="inline" /> إضافة</button>
          <button onClick={() => setF({ ...f, sign: -1 })} data-testid="points-deduct" className={`flex-1 h-11 rounded-full text-sm font-bold ${f.sign === -1 ? "bg-[#B45309] text-white" : "bg-white/5 border border-white/10 text-[#bcc8cf]"}`}><Minus size={14} className="inline" /> خصم</button>
        </div>
        <div><label className="v2-label">عدد النقاط</label><input type="number" min="1" value={f.points} onChange={(e) => setF({ ...f, points: e.target.value })} data-testid="points-value" className="v2-input" /></div>
        <div><label className="v2-label">سبب التعديل (إلزامي)</label><textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="points-reason" className="v2-input resize-none" placeholder="مثال: تصحيح خطأ في احتساب نقاط تحدي اليوم 5" /></div>
        <div className="flex items-start gap-2 v2-inset p-3 text-xs text-[#e3c799]">
          <ShieldAlert size={16} className="shrink-0 mt-0.5" />
          يُسجَّل كل تعديل يدوي في سجل الإدارة مع اسمك والتاريخ والوقت والسبب، ويُبلَّغ الموظف بإشعار.
        </div>
      </div>
      <button onClick={save} disabled={saving} data-testid="points-save" className="v2-btn w-full mt-5">{saving ? "جارٍ التنفيذ..." : "تنفيذ التعديل"}</button>
    </Modal>
  );
}

export default function Points() {
  const { user: admin } = useAuth();
  const [type, setType] = useState("");
  const [modal, setModal] = useState(false);
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["adm-points", type],
    queryFn: async () => (await api.get(`/admin/points?tx_type=${type}&limit=200`)).data,
  });

  return (
    <div className="space-y-5" data-testid="admin-points">
      <PageHead icon={Coins} title="مركز التحكم بالنقاط" sub="سجل كامل لكل عملية نقاط مع إمكانية الإضافة والخصم والتصحيح.">
        {can(admin, "manage_points") && (
          <button onClick={() => setModal(true)} data-testid="points-open" className="v2-btn v2-btn-sm"><Plus size={15} /> تعديل نقاط</button>
        )}
      </PageHead>

      <div className="flex gap-2 flex-wrap">
        {[["", "كل العمليات"], ["checkin", "حضور يومي"], ["challenge", "إكمال تحدٍ"], ["manual", "تعديل يدوي"]].map(([k, l]) => (
          <button key={l} onClick={() => setType(k)} data-testid={`points-filter-${k || "all"}`}
            className={`px-4 h-9 rounded-full text-xs font-bold ${type === k ? "bg-gradient-to-l from-[#e3c799] to-[#bb9661] text-[#25343f]" : "bg-white/5 border border-white/10 text-[#bcc8cf]"}`}>{l}</button>
        ))}
      </div>

      {isLoading ? <Skeleton className="h-72 w-full" /> : rows.length === 0 ? (
        <GlassCard className="p-10 text-center text-[#bcc8cf] text-sm">لا توجد عمليات.</GlassCard>
      ) : (
        <Table head={["التاريخ", "الموظف", "النوع", "النقاط", "السبب / التحدي", "بواسطة"]} min={800} testId="points-table">
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="text-[#bcc8cf] text-xs" dir="ltr">{(r.at || "").slice(0, 16).replace("T", " ")}</td>
              <td className="font-semibold">{r.user_name}</td>
              <td><Chip tone={r.type === "manual" ? "gold" : "muted"}>{TYPE_AR[r.type] || r.type}</Chip></td>
              <td className={`font-extrabold ${r.points >= 0 ? "v2-emerald-text" : "text-[#F7A08A]"}`}>{r.points > 0 ? "+" : ""}{r.points}</td>
              <td className="text-[#bcc8cf] max-w-[280px] truncate">{r.reason || r.challenge_title || "—"}</td>
              <td className="text-xs text-[#bcc8cf]">{r.by || "النظام"}</td>
            </tr>
          ))}
        </Table>
      )}

      {modal && <AdjustModal onClose={() => setModal(false)} />}
    </div>
  );
}
